import PropTypes from 'prop-types';
import { useEffect } from 'react';
import { enqueueSnackbar, closeSnackbar } from 'notistack';
// @mui
import Button from '@mui/material/Button';
import IconButton from '@mui/material/IconButton';
//
import Iconify from '../iconify';
import { buildIdFromHtml, currentBuildId, fetchLatestIndexHtml } from './app-version';

// ----------------------------------------------------------------------

export const CHECK_EVERY_MS = 5 * 60 * 1000;
// Focus and visibilitychange fire together when the app comes back to the front.
export const MIN_GAP_MS = 60 * 1000;

const SNACKBAR_KEY = 'app-update-available';

// An open tab, or the installed app resumed from the background, keeps running the code it
// first loaded. This checks for a newer deploy when the app comes back to the front and
// every few minutes while it is visible, and offers a reload. It does not reload on its
// own, so a half-filled form is never lost.
export default function AppUpdateNotifier({ fetchHtml = fetchLatestIndexHtml }) {
  useEffect(() => {
    const running = currentBuildId();
    if (!running) return undefined;

    let lastCheck = Date.now();
    let notified = false;
    let unmounted = false;

    const check = async () => {
      if (notified || document.visibilityState === 'hidden') return;
      const now = Date.now();
      if (now - lastCheck < MIN_GAP_MS) return;
      lastCheck = now;

      let latest;
      try {
        latest = buildIdFromHtml(await fetchHtml());
      } catch (error) {
        // Offline, or mid-deploy. The next check tries again.
        return;
      }
      if (unmounted || !latest || latest === running) return;

      notified = true;
      enqueueSnackbar('A new version of MyFVSD is available.', {
        key: SNACKBAR_KEY,
        variant: 'info',
        persist: true,
        action: (id) => (
          <>
            <Button
              size="small"
              color="info"
              variant="contained"
              onClick={() => window.location.reload()}
              sx={{ mr: 1 }}
            >
              Reload
            </Button>
            <IconButton size="small" onClick={() => closeSnackbar(id)} sx={{ p: 0.5 }}>
              <Iconify width={16} icon="mingcute:close-line" />
            </IconButton>
          </>
        ),
      });
    };

    const interval = setInterval(check, CHECK_EVERY_MS);
    document.addEventListener('visibilitychange', check);
    window.addEventListener('focus', check);

    return () => {
      unmounted = true;
      clearInterval(interval);
      document.removeEventListener('visibilitychange', check);
      window.removeEventListener('focus', check);
    };
  }, [fetchHtml]);

  return null;
}

AppUpdateNotifier.propTypes = {
  fetchHtml: PropTypes.func,
};
